"use client";

import { useEffect, useState } from "react";
import { Loader2, Users, X } from "lucide-react";

import { Button } from "@/components/ui";

// Who can open preview.vbrain.io.
// INTENT: our own app login is meant to be the only gate on that address, so
// the honest answer to "who may see Preview" is "who is approved in the app",
// MEASURED 21.09.2026: Vercel intercepted it (302 -> vercel.com/sso-api), because
// "all_except_custom_domains" exempts PRODUCTION custom domains only and this one
// is bound to gitBranch "preview". CLOSED 22.09.2026 with Gal's approval by adding
// the domain to Deployment Protection Exceptions; re-measured 307 -> /login 200.
// and that list is exactly what this panel shows. `can_access_dev_shell` is
// reported beside it as the separate, narrower capability: it decides who sees
// the dev bar at all, not who can reach the Preview address.
//
// Colors: role classes + tokens only (Rule 24 / palette ratchet). This panel is
// a new screen file, so it carries zero raw palette classes by construction.

export type PreviewViewer = {
  id: string;
  email: string;
  name: string;
  role: string;
  approved: boolean;
  accessStatus: string;
  blocked: boolean;
  devShell: boolean;
};

const SCRIM = { background: "color-mix(in srgb, var(--bg-app) 72%, transparent)" } as const;
const PANEL = { background: "var(--bg-chrome)", borderColor: "var(--border-popover)" } as const;
const HAIRLINE = { borderColor: "var(--border-hairline)" } as const;
const MUTED = { color: "var(--fg-muted)" } as const;
const FAINT = { color: "var(--fg-faint)" } as const;
const FG = { color: "var(--fg-default)" } as const;
const NOTE = {
  borderColor: "var(--cc-accent-500-30)",
  background: "var(--cc-accent-500-15)",
  color: "var(--fg-muted)",
} as const;

export function PreviewViewersOverlay({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<PreviewViewer[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    fetch("/api/access/preview-viewers", { cache: "no-store" })
      .then(async (r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (alive) setRows(d.users ?? []); })
      .catch((e: Error) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, []);

  const approved = rows?.filter((r) => r.approved) ?? [];
  const others = rows?.filter((r) => !r.approved) ?? [];

  return (
    <div
      dir="rtl"
      onClick={onClose}
      style={SCRIM}
      className="fixed inset-x-0 bottom-0 top-9 z-[57] flex justify-center overflow-y-auto backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={PANEL}
        className="my-4 h-fit w-full max-w-2xl rounded-2xl border p-4 shadow-2xl"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 style={FG} className="flex items-center gap-2 text-base font-semibold">
            <Users size={15} className="app-accent-text-soft" /> מי רשאי לראות Preview
          </h2>
          <Button variant="ghost" size="sm" icon={X} onClick={onClose}>
            סגור
          </Button>
        </div>

        <p style={NOTE} className="mb-3 rounded-lg border p-2 text-[11px] leading-5">
          שער ההתחברות שלנו הוא היחיד על הכתובת{" "}
          <span className="app-accent-text-soft font-mono">preview.vbrain.io</span>, ולכן מי שמאושר כאן
          יכול להיכנס. נמדד 22.09.2026 אחרי שהכתובת נוספה לרשימת החריגים של הגנת Vercel באישור גל.
          העמודה «בורר» היא הרשאה נפרדת וצרה יותר: היא קובעת מי רואה את הסרגל הזה.
        </p>

        {err && (
          <div style={HAIRLINE} className="text-error rounded-lg border p-2 text-[11px]">
            שגיאה בטעינה: {err}
          </div>
        )}
        {!rows && !err && (
          <div style={MUTED} className="flex items-center gap-2 p-2 text-[11px]">
            <Loader2 size={12} className="animate-spin" /> טוען…
          </div>
        )}

        {rows && (
          <div className="space-y-3">
            <Section title={`מאושרים — ${approved.length}`} rows={approved} tone="ok" />
            {others.length > 0 && (
              <Section title={`לא מאושרים — ${others.length}`} rows={others} tone="muted" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, rows, tone }: { title: string; rows: PreviewViewer[]; tone: "ok" | "muted" }) {
  return (
    <div>
      <div
        style={tone === "ok" ? undefined : MUTED}
        className={`mb-1 text-[11px] font-medium ${tone === "ok" ? "text-success" : ""}`}
      >
        {title}
      </div>
      <div style={HAIRLINE} className="overflow-hidden rounded-lg border">
        {rows.length === 0 && (
          <div style={FAINT} className="p-2 text-[11px]">אין</div>
        )}
        {rows.map((u) => (
          <div
            key={u.id}
            style={HAIRLINE}
            className="flex items-center gap-2 border-b px-2 py-1 text-[11px] last:border-b-0"
          >
            <span style={FG} className="min-w-0 flex-1 truncate">{u.name || u.email || u.id}</span>
            <span style={FAINT} className="min-w-0 flex-1 truncate font-mono text-[10px]">{u.email}</span>
            {u.blocked && (
              <span style={HAIRLINE} className="text-error shrink-0 rounded border px-1 text-[10px]">חסום</span>
            )}
            {!u.approved && !u.blocked && (
              <span style={HAIRLINE} className="text-warning shrink-0 rounded border px-1 text-[10px]">
                {u.accessStatus || "—"}
              </span>
            )}
            {u.devShell && (
              <span style={HAIRLINE} className="app-accent-text-soft shrink-0 rounded border px-1 text-[10px]">
                בורר
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
